import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { ArrowRight, MessageSquare } from "@/components/icons"
import { Spinner } from "@/components/ui/spinner"
import { useRoutes } from "@/contexts/use-routes"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"

interface ProjectChatHistoryProps {
  projectId: string
  sessionType: string
}

export function ProjectChatHistory({ projectId, sessionType }: ProjectChatHistoryProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getRouteChatPath } = useRoutes()

  const { data: sessions = [], isLoading } = useSessionsQuery(sessionType, projectId)

  const handleProjectSessionClick = (session: NonNullable<(typeof sessions)[0]>) => {
    // Navigate to global chat URL for the current module's sessionType
    // (not the session's original sessionType)
    const chatPath = getRouteChatPath(sessionType)
    void navigate(`${chatPath}/${session.id}`)
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="mb-6">
          <h2 className="text-muted-foreground text-sm font-medium">
            {t("projects.chat.history.recent_conversations")}
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner className="size-6" />
        </div>
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="bg-muted/50 mb-4 rounded-full p-4">
          <MessageSquare className="text-muted-foreground/60 size-6" />
        </div>
        <h3 className="mb-2 text-base font-medium">
          {t("projects.chat.history.no_conversations_title")}
        </h3>
        <p className="text-muted-foreground max-w-sm text-sm">
          {t("projects.chat.history.no_conversations_description")}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="mb-6">
        <h2 className="text-muted-foreground text-sm font-medium">
          {t("projects.chat.history.recent_conversations")}
        </h2>
      </div>
      <section className="flex h-full max-h-[calc(100vh-350px)] flex-col gap-2 overflow-y-auto pr-1 sm:max-h-[calc(100vh-425px)] sm:pr-3">
        {sessions.map((session) => {
          if (!session?.id) return null
          return (
            <button
              key={session.id}
              className="group bg-background/60 hover:bg-background/80 focus-visible:ring-ring relative w-full cursor-pointer rounded-lg p-4 text-left transition-[background-color,box-shadow] duration-200 hover:shadow-sm focus-visible:ring-1"
              type="button"
              onClick={() => handleProjectSessionClick(session)}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="text-foreground truncate text-sm font-medium transition-colors">
                    {session.title || t("projects.chat.history.untitled_conversation")}
                  </h3>
                  {session.updated && (
                    <p className="text-muted-foreground/70 mt-1 text-xs">
                      {new Date(session.updated).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  )}
                </div>
                <ArrowRight className="text-muted-foreground/60 group-hover:text-foreground size-4 transition-[color,translate] duration-200 group-hover:translate-x-1" />
              </div>
            </button>
          )
        })}
      </section>
    </div>
  )
}
